import { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { formatMoney } from '../../utils/format';

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Nairobi',
  year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export default function TenantTransferModal({ unit, onClose, onTransferred }) {
  const [preview, setPreview] = useState(null);
  const [destinationUnitId, setDestination] = useState('');
  const [moveDate, setMoveDate] = useState(today);
  const [rentAmount, setRentAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(false);
  const [loading, setLoading] = useState(true);
  const request = useRef(null);
  useEffect(() => {
    let active = true;
    api.get(`/frp/tenant-transfers/${unit._id}/preview`).then(({ data }) => {
      if (active) { setPreview(data); setRentAmount(String(data.rentAmount)); }
    }).catch((err) => { if (active) setError(err.response?.data?.error || 'Could not load the transfer.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [unit._id]);
  const destination = preview?.destinations.find((item) => item._id === destinationUnitId);
  const adjusted = preview && Number(rentAmount) !== preview.rentAmount;
  const valid = destination && moveDate && rentAmount !== '' && Number.isFinite(Number(rentAmount)) && Number(rentAmount) >= 0 && (!adjusted || reason.trim());

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    if (!review) { setReview(true); return; }
    setBusy(true); setError('');
    // Freeze the exact request after the first attempt, including on a network
    // timeout. A retry must never silently become a second transfer.
    if (!request.current) request.current = { destinationUnitId, moveDate, rentAmount: Number(rentAmount),
      reason: reason.trim(), previewKey: preview.previewKey,
      requestKey: `tenant-transfer-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}` };
    try {
      await api.post(`/frp/tenant-transfers/${unit._id}`, request.current);
      onTransferred();
    } catch (err) {
      setError(err.response?.data?.error || 'The result is not confirmed. Retry this transfer.');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <section role="dialog" aria-modal="true" aria-labelledby="transfer-title" className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
        <h2 id="transfer-title" className="text-lg font-bold">Transfer Tenant</h2>
        <p className="mt-1 text-sm text-gray-600">From Unit {unit.label}. Choose another unit in this property. No deposit is charged.</p>
        {error && <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        {loading && <p className="my-4">Loading balances…</p>}
        {preview && <form onSubmit={submit} className="mt-4 space-y-4">
          <fieldset hidden={review} disabled={busy || review || Boolean(request.current)} className="space-y-4 disabled:opacity-75">
            <label className="block text-sm">New unit
              <select required value={destinationUnitId} onChange={(e) => setDestination(e.target.value)} className="mt-1 w-full rounded border p-2">
                <option value="">Choose a vacant unit</option>
                {preview.destinations.map((item) => <option key={item._id} value={item._id}>Unit {item.label} — {formatMoney(item.rentAmount)} / month</option>)}
              </select>
            </label>
            {!preview.destinations.length && <p className="text-sm">There are no vacant units available in this property.</p>}
            <label className="block text-sm">Move date
              <input required type="date" max={today()} value={moveDate} onChange={(e) => setMoveDate(e.target.value)} className="mt-1 w-full rounded border p-2" />
            </label>
            <label className="block text-sm">Rent amount
              <input required type="number" min="0" max="1000000000" step="0.01" value={rentAmount} onChange={(e) => setRentAmount(e.target.value)} className="mt-1 w-full rounded border p-2" />
              <span className="mt-1 block text-xs text-gray-600">Old unit rent debt: {formatMoney(preview.rentAmount)}. Adjust this amount for the move if needed.</span>
            </label>
            {preview.rentBalance < 0 && <p className="text-sm">Rent credit of {formatMoney(-preview.rentBalance)} stays on the old statement.</p>}
            <label className="block text-sm">Reason {adjusted ? '(required for the rent change)' : '(optional)'}
              <textarea maxLength={500} required={Boolean(adjusted)} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded border p-2" />
            </label>
          </fieldset>
          <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-2">
            <p className="font-semibold">Debts moving to the new unit</p>
            {preview.utilityDebts.map((bill) => <p key={`${bill.invoiceModel}:${bill.invoiceId}`} className="break-words">{bill.description || bill.utilityType}: {formatMoney(bill.amount)}</p>)}
            {preview.unbilledWaterDebt > 0 && <p>Unbilled water meter debt: {formatMoney(preview.unbilledWaterDebt)}</p>}
            <p>Rent: {formatMoney(Number(rentAmount) || 0)}</p>
            <p>Utilities and water: {formatMoney(preview.utilityAmount)}</p>
            <p className="font-semibold">Total debt: {formatMoney((Number(rentAmount) || 0) + preview.utilityAmount)}</p>
            {destination && <p>Future monthly rent: {formatMoney(destination.rentAmount)}</p>}
            {review && reason.trim() && <p className="break-words">Reason: {reason.trim()}</p>}
            <p>No wallet money is taken. Old records stay available.</p>
          </div>
          {review && <p className="rounded bg-amber-50 p-3 text-sm">Confirm the move from Unit {unit.label} to Unit {destination.label} on {moveDate}. Management completes this transfer without tenant approval.</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" disabled={busy} onClick={onClose} className="rounded border px-4 py-2">Close</button>
            {review && !request.current && <button type="button" onClick={() => setReview(false)} className="rounded border px-4 py-2">Edit</button>}
            <button disabled={busy || !valid} className="rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">{busy ? 'Transferring…' : review ? 'Confirm Transfer' : 'Review Transfer'}</button>
          </div>
        </form>}
        {!preview && !loading && <button onClick={onClose} className="mt-4 rounded border px-4 py-2">Close</button>}
      </section>
    </div>
  );
}
